import { z } from 'zod';

export const DIAGNOSTIC_KINDS = [
  'global_error',
  'unhandled_rejection',
  'connection_error',
  'connection_timeout',
  'connection_closed',
  'connection_latency',
  'submission_waiting',
  'submission_error',
  'submission_recovered',
  'round_start_late',
] as const;
/** Longest duration a basic report can carry; reporters clamp to it. */
export const MAX_DIAGNOSTIC_DURATION_MS = 60_000;
const basicDiagnosticSchema = z
  .object({
    kind: z.enum(DIAGNOSTIC_KINDS),
    durationMs: z.number().int().min(0).max(MAX_DIAGNOSTIC_DURATION_MS).optional(),
  })
  .strict();
/** Numeric window summaries only: never event entries, words or gesture coordinates. */
const summarySchema = z
  .object({
    count: z.number().int().min(1).max(10_000),
    totalMs: z.number().int().min(0).max(600_000_000),
    maxMs: z.number().int().min(0).max(60_000),
    slowCount: z.number().int().min(0).max(10_000),
  })
  .strict()
  .refine((value) => value.slowCount <= value.count && value.maxMs <= value.totalMs);
export const gameplayPerformanceSchema = z
  .object({
    kind: z.literal('gameplay_performance'),
    windowMs: z.number().int().min(0).max(60_000),
    inputFrame: summarySchema.optional(),
    frameGap: summarySchema.optional(),
    longTask: summarySchema.optional(),
    wordAck: summarySchema.optional(),
    submissionFeedback: summarySchema.optional(),
  })
  .strict()
  .refine(
    (value) =>
      value.inputFrame || value.frameGap || value.longTask || value.wordAck || value.submissionFeedback,
  );
export type GameplayPerformanceReport = z.infer<typeof gameplayPerformanceSchema>;
export const diagnosticSchema = z.union([basicDiagnosticSchema, gameplayPerformanceSchema]);
export type Diagnostic = z.infer<typeof basicDiagnosticSchema>;
